import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IoAdd } from 'react-icons/io5';
import axios from 'config/axios';
import Header from 'components/Header';
import Button from 'components/Button';
import Tabs from 'components/Tabs';
import Dropdown from 'components/Dropdown';
import DropdownSelect from 'components/DropdownSelect';
import TextInput from 'components/TextInput';
import Popup from 'components/Popup';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import './index.css';

const TEST_TYPES = ['Paper ACT'];
const TEST_SECTIONS = {
    'Paper ACT': {
        'ACT English': true,
        'ACT Mathematics': true,
        'ACT Reading': true,
        'ACT Science': true,
    },
}
const NUM_QUESTIONS = {
    'Paper ACT': {
        'ACT English': 50,
        'ACT Mathematics': 45,
        'ACT Reading': 36,
        'ACT Science': 40,
    }
}

function UploadTest() {
    const [testName, setTestName] = useState('');
    const [testType, setTestType] = useState(TEST_TYPES[0]);
    const [testSections, setTestSections] = useState(TEST_SECTIONS[testType]);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [next, setNext] = useState(false);
    const [questions, setQuestions] = useState({});
    const [categories, setCategories] = useState({});
    const [creating, setCreating] = useState(false);
    const [alert, setAlert] = useState(null);

    const activeTestSections = Object.keys(testSections).filter(section => testSections[section]);

    const navigate = useNavigate();

    const alertRef = useOutsideClickHandler(() => setAlert(null));

    const initializeSections = () => {
        let initialQuestions = {};

        for (let i = 0; i < activeTestSections.length; i++) {
            initialQuestions = {
                ...initialQuestions,
                [activeTestSections[i]]: Array.from({
                        length: NUM_QUESTIONS[testType][activeTestSections[i]]
                    }, () => ({
                        correctAnswer: null,
                        category: null
                    }))
            };
        }
        
        setQuestions(initialQuestions);
        setNext(true);
    }

    const setCorrectAnswer = (questionIndex: number, answer: string) => {
        let newQuestions = { ...questions };

        if (newQuestions[activeTestSections[sectionIndex]][questionIndex].correctAnswer === answer) {
            newQuestions[activeTestSections[sectionIndex]][questionIndex].correctAnswer = null;
        }
        else {
            newQuestions[activeTestSections[sectionIndex]][questionIndex].correctAnswer = answer;
        }
        
        setQuestions(newQuestions);
    }

    const setCategory = (questionIndex: number, category: string) => {
        let newQuestions = { ...questions };

        newQuestions[activeTestSections[sectionIndex]][questionIndex].category = category;

        setQuestions(newQuestions);
    }

    const uploadTest = () => {
        setCreating(true);

        if (Object.values(questions).find(section => Array.isArray(section) && section.find(question => question.correctAnswer === null))) {
            setAlert('Make sure you fill in all the answers.');
            setCreating(false);
        }
        else {
            axios.post('tutors/tests/upload/', {
                    testName: testName,
                    testType: testType,
                    questions: questions
                })
                .then(res => {
                    setCreating(false);
                    navigate('/tests');
                })
                .catch(error => {
                    setCreating(false);
                    console.log(error);
                });
        }
    }

    useEffect(() => {
        if (next) {
            axios.get(`tutors/tests/category/${testType}`)
                .then(res => {
                    // console.log(res.data);
                    setCategories(res.data.categories);
                });
        }
    }, [next]);

    if (!next) {
        return (
            <div className='uploadTest'>
                <Helmet>
                    <title>
                        Upload Test - Test Center Admin
                    </title>
                </Helmet>
                <Header title='Upload Test' />
                <div className='uploadTest__bar'>
                    <h3 className='uploadTest__header'>Upload a custom test</h3>
                    <Button
                        className='uploadTest__button'
                        onClick={initializeSections}
                        disabled={!testName || activeTestSections.length === 0}
                    >
                        Next
                    </Button>
                    <Button className='uploadTest__button' onClick={() => navigate('/tests')} secondary>
                        Cancel
                    </Button>
                </div>
                <TextInput
                    className='uploadTest__choice'
                    label='Test Name'
                    value={testName}
                    onChange={event => setTestName(event.target.value)}
                    required
                />
                <Dropdown
                    className='uploadTest__choice'
                    data={TEST_TYPES}
                    onSelect={(value) => setTestType(value)}
                    label='Test Type'
                    required
                    value={testType}
                />
                <DropdownSelect
                    className='uploadTest__choice'
                    data={Object.keys(testSections)}
                    selected={Object.values(testSections)}
                    label='Test Sections'
                    onSelect={value => setTestSections(prev => ({
                        ...prev,
                        [value]: !prev[value]
                    }))}
                />
            </div>
        );
    }

    return (
        <div className='uploadTest'>
            <Helmet>
                <title>
                    Upload Test - Test Center Admin
                </title>
            </Helmet>
            {alert &&
                <Popup
                    children={<p>{alert}</p>}
                    primaryButtonText='Close'
                    primaryButtonOnClick={() => setAlert(null)}
                    innerRef={alertRef}
                />
            }
            <Header title='Upload Test' />
            <div className='uploadTest__bar'>
                <h3 className='uploadTest__header'>{testName}</h3>
                <Button className='uploadTest__button' onClick={uploadTest} disabled={creating}>
                    Create
                </Button>
                <Button className='uploadTest__button' onClick={() => navigate('/tests')} secondary>
                    Cancel
                </Button>
            </div>
            <Tabs
                className='uploadTest__tabs'
                tabs={activeTestSections.map(testSection => testSection)}
                tabIndex={sectionIndex}
                onSelect={activeTestSections.map((_, index) => (() => setSectionIndex(index)))}
            />
            {/* <TextInput
                className='uploadTest__sectionName'
                label='Section Name'
                value={sections[sectionIndex].name}
                onChange={event => setSectionName(event.target.value)}
                required
            /> */}
            <p className='uploadTest__directions'>Mark the correct answer for each question.</p>
            {questions[activeTestSections[sectionIndex]].map((question, index) => (
                <div className='uploadTest__question'>
                    <p className='uploadTest__questionNumber'>{index + 1}</p>
                    <div className='uploadTest__bubbles'>
                        <p className={question.correctAnswer === 'A'
                            ? 'uploadTest__bubble--selected'
                            : 'uploadTest__bubble'
                        } onClick={() => setCorrectAnswer(index, 'A')}>A</p>
                        <p className={question.correctAnswer === 'B'
                            ? 'uploadTest__bubble--selected'
                            : 'uploadTest__bubble'
                        } onClick={() => setCorrectAnswer(index, 'B')}>B</p>
                        <p className={question.correctAnswer === 'C'
                            ? 'uploadTest__bubble--selected'
                            : 'uploadTest__bubble'
                        } onClick={() => setCorrectAnswer(index, 'C')}>C</p>
                        <p className={question.correctAnswer === 'D'
                            ? 'uploadTest__bubble--selected'
                            : 'uploadTest__bubble'
                        } onClick={() => setCorrectAnswer(index, 'D')}>D</p>
                        <p className={question.correctAnswer === 'E'
                            ? 'uploadTest__bubble--selected'
                            : 'uploadTest__bubble'
                        } onClick={() => setCorrectAnswer(index, 'E')}>E</p>
                    </div>
                    {categories[activeTestSections[sectionIndex]] &&
                        <Dropdown
                            className='uploadTest__categoryDropdown'
                            data={categories[activeTestSections[sectionIndex]]}
                            onSelect={(value) => setCategory(index, value)}
                            value={question.category}
                        />
                    }
                    
                </div>
            ))}
        </div>
    );
}

export default UploadTest;